/*
 * myPokemonStore.ts
 *
 * The device-first **My Pokémon** database: one record per individual caught
 * Pokémon (a real Slaking with ITS moveset, CP and IVs — not the species-level
 * dex state `dexStateStore` carries; that file's header explicitly scopes
 * instance fields OUT of the dex store, and this is where they live instead).
 *
 * Records are written by the live scanner from the caught-Pokémon page
 * (`myPokemonOcr.ts`), and the page has TWO views the user flips between
 * (detail vs appraisal overlay), so records are built up from PARTIAL
 * observations that merge over time:
 *
 *   - A detail-view read creates the record (species/nickname, CP, HP, moves,
 *     provenance…), with `ivAtk/ivDef/ivHp` still null.
 *   - The appraisal-view read of the SAME Pokémon (matched by species + CP +
 *     max HP) merges the IVs into that record rather than creating a twin.
 *   - A field once read is never blanked by a frame that didn't see it: merge
 *     is "non-null incoming wins", never "null overwrites".
 *
 * Storage is a single localStorage JSON blob, like `dexStateStore` /
 * `scanSessionStore` — device-local, v1 has no server sync. The record shape
 * is deliberately sync-ready (client-minted UUID id + device-clock
 * `updatedAt`) so a future D1-backed backup can adopt the dex sync contract
 * (client_updated_at LWW) without a client migration.
 *
 * This intentionally does NOT touch the dormant `pokemon_records` server
 * table or the legacy `InventoryRecord` CSV model — see docs in the plan;
 * both belong to the abandoned deep-scan pipeline.
 */

import { megaFormKeyForLabel } from "./megaLevels";
import { useSyncExternalStore } from "react";
import { nicknameCompareKey } from "./ivNicknames";
import { evolutionChainFor } from "./generated/speciesBaseStats.generated";
import { computeCp, computeHp } from "./ivSolver";
import { baseStatsForRecord } from "./myPokemonDerived";
import { cpFromHp } from "./cpFromHp";
import { evolvedFormIdFor, formCanEvolveTo } from "./costumeEvolution";
import { typesFor } from "./types";
import { myPokemonTypes } from "./myPokemonTypes";
import { speciesName as speciesNameFor } from "../data/speciesNames";

export const MY_POKEMON_EVENT = "nexus:my-pokemon-changed";
const STORAGE_KEY = "nexus.myPokemon.v1";
/** Storage safety cap, above PoGo's own ~9.5k storage max so a full
 * Poké Genie CSV import (thousands of rows) always fits. */
const MAX_RECORDS = 10000;

export type MyPokemonGender = "male" | "female" | null;

/**
 * HOW a record came to exist (2026-09-16 owner ask: "how are people adding to
 * My Pokémon?"). Set once at creation by the path that created it and never
 * changed by a later merge; rides the backup payload to the server, where the
 * admin journey counts adds per path. Absent on records from older builds.
 *   live_scan   the HUD over GO (useDexLiveScan)
 *   screenshot  screenshots chosen from the library on the My Pokémon page
 *   auto_scan   the camera-roll Auto Scan
 *   manual      typed in by hand (the add sheets)
 *   import      a Poké Genie CSV
 *   evolve      minted by the Evolve action from an existing record
 */
export type MyPokemonAddedVia = "live_scan" | "screenshot" | "auto_scan" | "manual" | "import" | "evolve";
export const MY_POKEMON_ADDED_VIA: readonly MyPokemonAddedVia[] = [
  "live_scan",
  "screenshot",
  "auto_scan",
  "manual",
  "import",
  "evolve",
];

export interface MyPokemonProvenance {
  kind: "wild" | "raid" | "trade" | "egg" | "location" | "other";
  label: string;
  value: string | null;
  dateRaw: string | null;
  dateIso: string | null;
}

export interface MyPokemonMoveRecord {
  name: string;
  power: number | null;
  kind: "fast" | "charged";
}

/**
 * THIS Pokémon's Max Moves (2026-09-04 owner ask, from the Zacian and
 * Zamazenta screenshots): GO's three tiles under "Max Moves". Levels are
 * 1..3 (GO prints 3 as "MAX!"); Guard and Spirit start LOCKED, which is
 * level 0. A slot that has never been read is absent. `attack.name` is the
 * move printed under the tile ("Behemoth Bash", "Max Flare"), null when the
 * tile's level was read but its name was not.
 */
export interface MyPokemonMaxMoves {
  attack?: { name: string | null; level: 1 | 2 | 3 };
  guard?: 0 | 1 | 2 | 3;
  spirit?: 0 | 1 | 2 | 3;
}

/** What a scan read off the Max Moves panel: the ribbon alone, or the tiles. */
export interface MyPokemonMaxMovesRead extends MyPokemonMaxMoves {
  /** The page printed the "Max Moves" ribbon: this Pokémon has Max Moves. */
  hasMaxMoves: boolean;
}

export interface MyPokemonRecord {
  /** Client-minted UUID — the record's permanent identity. */
  id: string;
  speciesId: number | null;
  speciesName: string | null;
  /** User nickname when the printed name was not a species name. */
  nickname: string | null;
  cp: number | null;
  hpMax: number | null;
  /** The CP the trainer typed by hand (2026-09-11). While it equals `cp`, the
   *  sanitizer leaves the CP alone even when the IVs and HP disagree with it. */
  cpManual?: number | null;
  /** True while `cp` was CALCULATED from the IVs and max HP rather than
   *  read or typed (cpFromHp, 2026-09-20). A scan that reads the real CP
   *  may then correct it (findMatchingRecord tolerates the difference) and
   *  clears the flag. Optional so existing records parse. */
  cpDerived?: boolean;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  /** Gold stars on the appraisal seal (0-3), or null when never appraised on-scan. */
  appraisalStars: number | null;
  moves: MyPokemonMoveRecord[];
  gender: MyPokemonGender;
  weightKg: number | null;
  heightM: number | null;
  /** GO's size class badge, read off the detail page ("XXS"/"XS"/"XL"/"XXL"). */
  sizeClass: MyPokemonSizeClass;
  types: string[];
  isLucky: boolean;
  /** Room for detectors that don't exist yet — never inferred, only set. */
  isShiny: boolean;
  isShadow: boolean;
  isPurified: boolean;
  /** Best Buddy (2026-08-31): hand-set only, no scan detector. Feeds the
   * level-51 PvP cap on the detail page. Optional so existing records
   * parse; compactRecord drops it while false. */
  isBestBuddy?: boolean;
  /** The buddy level (2026-09-20 owner ask): Good, Great, Ultra or Best,
   * hand-set on the record page. `isBestBuddy` stays the level-51 flag and
   * always agrees with this ("best" <=> isBestBuddy). Any level qualifies
   * for the buddy showcases. Absent when the Pokémon is not a buddy. */
  buddyLevel?: MyPokemonBuddyLevel | null;
  /** The MAX/MEGA axis (2026-09-02): "I have Mega-Evolved this one",
   * "this one can Dynamax", "this one can Gigantamax". Hand-set only, no
   * scan detector, and each one vouches its own curated dex (mega / dynamax
   * / gmax). Optional for the same reason isBestBuddy is. */
  isMega?: boolean;
  isDynamax?: boolean;
  isGmax?: boolean;
  /**
   * The STAR (2026-09-29 owner ask): a favourite, protected from deletion.
   * Every user-facing delete goes through deleteMyPokemonWithUndo, which
   * leaves a starred record alone unless forced, and Box Cleanup never
   * nominates one. Hand-set only (the record page's star). Optional for
   * the same reason isBestBuddy is: records predate it.
   */
  isFavourite?: boolean;
  /**
   * THIS Pokémon's Mega Level per Mega form (2026-09-03 owner ask), keyed
   * by megaFormKey ("6", "6:charizard-mega-y"): 1 Base, 2 High, 3 Max,
   * 4 Super Max. A Mega Level belongs to one Pokémon in GO, so it lives on
   * the record; setting it also raises the species-wide level the Mega
   * pages read (megaLevelStore). Hand-set only. Absent when never set.
   */
  megaLevels?: Record<string, 1 | 2 | 3 | 4>;
  /**
   * Max Moves (2026-09-04): the page's "Max Moves" ribbon proves this one
   * uses them (`hasMaxMoves`, also true for Crowned Zacian / Zamazenta and
   * Eternatus, which have Max Moves without Dynamaxing), and the tiles give
   * each slot's level. Levels only rise from a read; the attack name follows
   * the latest read (a Fast TM changes it without touching the level).
   */
  hasMaxMoves?: boolean;
  maxMoves?: MyPokemonMaxMoves;
  /** User tag pills read off the page ("GBL", ".BLUE", …). */
  tags: string[];
  caughtDateRaw: string | null;
  caughtDateIso: string | null;
  caughtLocation: string | null;
  provenance: MyPokemonProvenance[];
  /** Display-only form label from an import ("Alola", "Galarian"). */
  form: string | null;
  /** Canonical form id from the forms registry ("geodude-alola") — the
   * detail page's form picker writes it; null = the base form. Unlike
   * `form` (a free-text import label) this drives sprite resolution. */
  formId: string | null;
  source: "scan" | "manual" | "import";
  /** The path that created this record (2026-09-16). Absent on older records. */
  addedVia?: MyPokemonAddedVia;
  /**
   * Locked power-up goal for THIS Pokémon (2026-08-26): the form × league
   * cell the trainer tapped in the detail page's PvP IVs matrix. Optional so
   * every existing record parses; scans never touch it (merges spread the
   * existing record). Display/planning only — clearing it changes no stats.
   */
  pvpTarget?: { formSpeciesId: number; league: "great" | "ultra" | "master" } | null;
  /**
   * What this record was before the trainer pressed "Evolved it?" on the
   * detail page (2026-09-15 owner ask), so the evolution can be undone
   * without a rescan. Present only while an evolution is undoable; the next
   * scan or evolution replaces it.
   */
  evolvedFrom?: MyPokemonEvolutionSnapshot | null;
  createdAt: number;
  /** Device clock (the future sync `client_updated_at`). */
  updatedAt: number;
  /**
   * Device clock of the last SCAN or screenshot that wrote this record
   * (2026-09-20 owner ask). The list's default order is this, not
   * updatedAt, so editing a CP no longer jumps the row to the top and loses
   * the trainer's place. Absent on records from before; createdAt stands in.
   */
  scannedAt?: number;
}

export type MyPokemonBuddyLevel = "good" | "great" | "ultra" | "best";
export const MY_POKEMON_BUDDY_LEVELS: readonly MyPokemonBuddyLevel[] = ["good", "great", "ultra", "best"];
export const MY_POKEMON_BUDDY_LABELS: Record<MyPokemonBuddyLevel, string> = {
  good: "Good Buddy",
  great: "Great Buddy",
  ultra: "Ultra Buddy",
  best: "Best Buddy",
};

/** When a record was last scanned, for the list's default order. */
export function myPokemonScannedAt(r: Pick<MyPokemonRecord, "scannedAt" | "createdAt">): number {
  return r.scannedAt ?? r.createdAt;
}

/** The fields an evolution rewrites, kept so it can be put back. */
export interface MyPokemonEvolutionSnapshot {
  speciesId: number | null;
  speciesName: string | null;
  formId: string | null;
  cp: number | null;
  hpMax: number | null;
  cpManual: number | null;
  moves: MyPokemonMoveRecord[];
  types: string[];
  pvpTarget: MyPokemonRecord["pvpTarget"];
  isMega: boolean;
  megaLevels?: Record<string, 1 | 2 | 3 | 4>;
  /** Device clock at the evolution. */
  at: number;
}

/**
 * One partial observation of a Pokémon, in the shape `MyPokemonFrame`
 * produces (structurally compatible — the hook passes the frame straight in).
 * Everything is optional: a frame contributes what it read and nothing else.
 */
export type MyPokemonSizeClass = "xxs" | "xs" | "xl" | "xxl" | null;

export interface MyPokemonObservation {
  speciesId?: number | null;
  speciesName?: string | null;
  nickname?: string | null;
  cp?: number | null;
  hpMax?: number | null;
  iv?: { atk: number; def: number; hp: number; confidence: number } | null;
  /**
   * Facts the scan has seen but will not write (2026-10-07): the IV bars read
   * on this frame before the three-frame agreement, or earlier in the same
   * dwell, and the max HP read before the appraisal overlay hid it. They
   * only ever REFUSE a match, so a second Pokémon of the same species and
   * CP cannot be folded into the first one's record while its own HP or
   * IVs are off screen. Never copied onto a record.
   */
  ivVeto?: { atk: number; def: number; hp: number } | null;
  hpVeto?: number | null;
  appraisalStars?: number | null;
  moves?: MyPokemonMoveRecord[];
  gender?: MyPokemonGender;
  weightKg?: number | null;
  heightM?: number | null;
  sizeClass?: MyPokemonSizeClass;
  types?: string[];
  isLucky?: boolean;
  isShiny?: boolean;
  isShadow?: boolean;
  isPurified?: boolean;
  /** The Max band the caught-Pokémon page prints (myPokemonOcr). */
  isDynamax?: boolean;
  isGmax?: boolean;
  /**
   * The Mega Level circles the page showed per form (myPokemonOcr,
   * 2026-09-03): a filled circle proves this Pokémon has Mega Evolved to
   * that level. Raise-only into `megaLevels`, and sets isMega.
   */
  megaReads?: Array<{ formLabel: string; level: number; progressToNext?: number }>;
  /** The Max Moves panel as read (myPokemonOcr, 2026-09-04). Raise-only. */
  maxMoves?: MyPokemonMaxMovesRead | null;
  form?: string | null;
  /**
   * A resolved registry form id from a CSV row (2026-10-12): a Poké Genie
   * "Alola" label or a Nexus Dex template Costume cell, resolved by
   * csvFormResolve.ts. Set only when the row names one, so a merge never
   * clears a form the trainer picked by hand on the detail page.
   */
  formId?: string | null;
  /** The template's Mega 0/1 column. Raise-only, like isDynamax / isGmax. */
  isMega?: boolean;
  /** Only read when the observation CREATES a record; a merge never rewrites it. */
  addedVia?: MyPokemonAddedVia;
  tags?: string[];
  caughtDateRaw?: string | null;
  caughtDateIso?: string | null;
  caughtLocation?: string | null;
  provenance?: MyPokemonProvenance[];
}

export interface UpsertResult {
  record: MyPokemonRecord;
  /** True when this observation created a new record (vs merging into one). */
  created: boolean;
  /** True when the merge actually changed any stored field. */
  changed: boolean;
}

// ── id + storage plumbing ────────────────────────────────────────────────────

/** UUID with the WKWebView-safe fallback (same guard as newScanId). */
export function newMyPokemonId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `mypkm_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

interface StoreShape {
  records: MyPokemonRecord[];
}

let cache: MyPokemonRecord[] | null = null;

/**
 * Page chrome that leaked into stored records before the OCR filters existed
 * (2026-08-20 device passes: "WEATHER BONU"/"NEW ATTACK" as moves, "EIGHT"/
 * "HEAVIEST"/"XL" as tags). Filtered here at NORMALISE time so existing dirty
 * records self-clean on the next load — no migration needed. Move chrome is
 * matched case-sensitively against the page's ALL-CAPS labels so the real
 * move "Weather Ball" survives.
 */
// Case-SENSITIVE: the page prints chrome ALL-CAPS; "Weather Ball" must live.
const MOVE_CHROME_RE = /WEATHER|BONUS|NEW ATT|POWER UP|[#•·]/;
/** Our own HUD copy, any case. */
const MOVE_ECHO_RE = /pok[eé]mon|nexus/i;
/** Our HUD headline is fully UPPERCASE; real GO moves are Title Case. */
const isAllCapsEcho = (name: string) =>
  /[A-Z]{4}/.test(name) && name === name.toUpperCase();
const TAG_CHROME_RE = /EIGH|^(HP|HEAVIEST|LIGHTEST|TALLEST|SHORTEST|SMALLEST|XL|XS|XXL|XXS)$/i;

function normalizeRecord(raw: unknown): MyPokemonRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<MyPokemonRecord> & Record<string, unknown>;
  if (typeof r.id !== "string" || r.id.length === 0) return null;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const str = (v: unknown): string | null =>
    typeof v === "string" && v.length > 0 ? v : null;
  return {
    id: r.id,
    speciesId: num(r.speciesId),
    speciesName: str(r.speciesName),
    nickname: str(r.nickname),
    cp: num(r.cp),
    hpMax: num(r.hpMax),
    cpManual: num(r.cpManual),
    cpDerived: !!r.cpDerived,
    ivAtk: num(r.ivAtk),
    ivDef: num(r.ivDef),
    ivHp: num(r.ivHp),
    appraisalStars: num(r.appraisalStars),
    moves: Array.isArray(r.moves)
      ? (r.moves as MyPokemonMoveRecord[]).filter(
          (m) =>
            m &&
            typeof m.name === "string" &&
            m.name.length > 0 &&
            !MOVE_CHROME_RE.test(m.name) &&
            !MOVE_ECHO_RE.test(m.name) &&
            !isAllCapsEcho(m.name),
        )
      : [],
    gender: r.gender === "male" || r.gender === "female" ? r.gender : null,
    weightKg: num(r.weightKg),
    heightM: num(r.heightM),
    sizeClass:
      r.sizeClass === "xxs" || r.sizeClass === "xs" || r.sizeClass === "xl" || r.sizeClass === "xxl"
        ? r.sizeClass
        : null,
    types: Array.isArray(r.types)
      ? (r.types as unknown[]).filter((t): t is string => typeof t === "string")
      : [],
    isLucky: !!r.isLucky,
    isShiny: !!r.isShiny,
    isShadow: !!r.isShadow,
    isPurified: !!r.isPurified,
    isFavourite: !!r.isFavourite,
    // Best Buddy is the level-51 flag; the level agrees with it both ways so
    // a record from before the level existed reads as a Best Buddy still.
    isBestBuddy: !!r.isBestBuddy || r.buddyLevel === "best",
    buddyLevel: (MY_POKEMON_BUDDY_LEVELS as readonly unknown[]).includes(r.buddyLevel)
      ? (r.buddyLevel as MyPokemonBuddyLevel)
      : r.isBestBuddy
        ? "best"
        : null,
    isMega: !!r.isMega,
    isDynamax: !!r.isDynamax,
    isGmax: !!r.isGmax,
    megaLevels: readMegaLevels(r.megaLevels),
    hasMaxMoves: !!r.hasMaxMoves,
    maxMoves: readMaxMoves(r.maxMoves),
    tags: Array.isArray(r.tags)
      ? (r.tags as unknown[]).filter(
          (t): t is string => typeof t === "string" && !TAG_CHROME_RE.test(t.trim()),
        )
      : [],
    caughtDateRaw: str(r.caughtDateRaw),
    caughtDateIso: str(r.caughtDateIso),
    caughtLocation: str(r.caughtLocation),
    provenance: Array.isArray(r.provenance)
      ? (r.provenance as MyPokemonProvenance[]).filter(
          (p) => p && typeof p.label === "string",
        )
      : [],
    form: str(r.form),
    formId: str(r.formId),
    source: r.source === "manual" || r.source === "import" ? r.source : "scan",
    addedVia: (MY_POKEMON_ADDED_VIA as readonly unknown[]).includes(r.addedVia)
      ? (r.addedVia as MyPokemonAddedVia)
      : undefined,
    // Locked PvP power-up target (2026-08-26). Validated shape-first: a
    // mangled blob degrades to "no target", never to a crash.
    pvpTarget: (() => {
      const t = r.pvpTarget as { formSpeciesId?: unknown; league?: unknown } | null | undefined;
      if (
        t &&
        typeof t.formSpeciesId === "number" &&
        (t.league === "great" || t.league === "ultra" || t.league === "master")
      ) {
        return { formSpeciesId: t.formSpeciesId, league: t.league };
      }
      return null;
    })(),
    evolvedFrom: readEvolutionSnapshot(r.evolvedFrom),
    createdAt: num(r.createdAt) ?? 0,
    updatedAt: num(r.updatedAt) ?? 0,
    scannedAt: num(r.scannedAt) ?? undefined,
  };
}

/** Shape-first read of an evolution snapshot: a mangled blob is "nothing to undo". */
function readEvolutionSnapshot(raw: unknown): MyPokemonEvolutionSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);
  if (num(r.speciesId) == null) return null;
  const t = r.pvpTarget as { formSpeciesId?: unknown; league?: unknown } | null | undefined;
  return {
    speciesId: num(r.speciesId),
    speciesName: str(r.speciesName),
    formId: str(r.formId),
    cp: num(r.cp),
    hpMax: num(r.hpMax),
    cpManual: num(r.cpManual),
    moves: Array.isArray(r.moves)
      ? (r.moves as MyPokemonMoveRecord[]).filter((m) => m && typeof m.name === "string" && m.name.length > 0)
      : [],
    types: Array.isArray(r.types) ? (r.types as unknown[]).filter((x): x is string => typeof x === "string") : [],
    pvpTarget:
      t && typeof t.formSpeciesId === "number" && (t.league === "great" || t.league === "ultra" || t.league === "master")
        ? { formSpeciesId: t.formSpeciesId, league: t.league }
        : null,
    isMega: !!r.isMega,
    megaLevels: readMegaLevels(r.megaLevels),
    at: num(r.at) ?? 0,
  };
}

/**
 * COMPACT serialization: null / false / empty-array fields are omitted from
 * the stored JSON (normalizeRecord defaults them back on read), which cuts a
 * full Poké Genie import from ~1.5MB to ~0.8MB.
 */
function compactRecord(r: MyPokemonRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r)) {
    if (v === null || v === undefined || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}

// ── Persistence backend: IndexedDB, localStorage fallback ────────────────────
//
// WHY INDEXEDDB (2026-08-20 device report, twice): the Poké Genie import
// failed `localStorage.setItem` on a real iPhone even in the compact format —
// WKWebView's ~5MB localStorage quota is SHARED across the origin, and the
// dex store + scan logs already occupy most of it. IndexedDB has orders of
// magnitude more headroom, so it now holds the records; the legacy
// localStorage blob is migrated in on first read and then DELETED, which
// returns its quota to the stores that still live there.
//
// The store's public API stays synchronous over the in-memory `cache`
// (useSyncExternalStore needs sync snapshots): reads hydrate the cache from
// IDB once (an `emit` re-renders subscribers when it lands — milliseconds),
// scan-path writes persist optimistically in the background, and the BULK
// import awaits its persist so a storage failure is reported, never assumed
// away. Environments without IndexedDB (jsdom tests, ancient WebViews) fall
// back to the old synchronous localStorage path unchanged.

const IDB_NAME = "nexus-my-pokemon";
const IDB_STORE = "records";
const IDB_KEY = "all";

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) {
        req.result.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("idb open failed"));
  });
}

async function idbLoad(): Promise<unknown[] | null> {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : null);
      req.onerror = () => reject(req.error ?? new Error("idb read failed"));
    });
  } finally {
    db.close();
  }
}

async function idbSave(compact: Record<string, unknown>[]): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(compact, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("idb write failed"));
      tx.onabort = () => reject(tx.error ?? new Error("idb write aborted"));
    });
  } finally {
    db.close();
  }
}

function normalizeList(raw: unknown[]): MyPokemonRecord[] {
  const records: MyPokemonRecord[] = [];
  for (const item of raw) {
    const rec = normalizeRecord(item);
    if (rec) records.push(rec);
  }
  return records;
}

/** The legacy localStorage blob, or null. Shared by the fallback path and the
 * one-time IDB migration. */
function readLegacyLocalStorage(): MyPokemonRecord[] | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoreShape>;
    if (!parsed || !Array.isArray(parsed.records)) return null;
    return normalizeList(parsed.records);
  } catch {
    return null;
  }
}

let hydrated = false;
let hydrating: Promise<void> | null = null;

/**
 * Merge the roster loaded from storage with records the in-memory cache
 * accumulated WHILE hydration was still in flight (2026-08-30 audit: the
 * hydration race). Before this, `readStore()` returned `[]` mid-hydration, a
 * mutation then did read-modify-write over that empty list, and either the
 * persist overwrote the stored roster with a single record or the hydration
 * assignment clobbered the fresh write - whichever landed second destroyed
 * the other side.
 *
 * Merge semantics, by record id:
 *   - a record written during hydration WINS over the loaded copy (it is the
 *     newer statement - e.g. a scan updating an existing record),
 *   - records only in the loaded roster are kept (nothing is lost),
 *   - brand-new during-hydration records go first (they are this session's
 *     freshest catches).
 * Deletions during hydration cannot be silently undone by this merge in
 * practice: a delete needs the record present in the cache to find it, and
 * mid-hydration the cache only holds this session's own writes.
 *
 * Pure and exported for tests (jsdom has no IndexedDB to drive the real
 * async path).
 */
export function mergeRosterOnHydration(
  loaded: MyPokemonRecord[],
  duringHydration: MyPokemonRecord[],
): MyPokemonRecord[] {
  if (duringHydration.length === 0) return loaded;
  const loadedIds = new Set(loaded.map((r) => r.id));
  const overrides = new Map(duringHydration.map((r) => [r.id, r]));
  const merged: MyPokemonRecord[] = [];
  for (const r of duringHydration) {
    if (!loadedIds.has(r.id)) merged.push(r);
  }
  for (const l of loaded) {
    merged.push(overrides.get(l.id) ?? l);
  }
  return merged.slice(0, MAX_RECORDS);
}

/** Load the cache from IDB once (migrating any legacy localStorage blob in,
 * then deleting it to hand its quota back). Subscribers re-render via emit. */
function ensureHydrated(): void {
  if (hydrated || hydrating || !idbAvailable()) return;
  hydrating = (async () => {
    try {
      const stored = await idbLoad();
      let loaded: MyPokemonRecord[];
      let migratedLegacy = false;
      if (stored != null) {
        loaded = normalizeList(stored).slice(0, MAX_RECORDS);
      } else {
        const legacy = readLegacyLocalStorage();
        loaded = (legacy ?? []).slice(0, MAX_RECORDS);
        migratedLegacy = legacy != null && legacy.length > 0;
      }
      // Hydration-race fix (2026-08-30): anything already in the cache was
      // written THIS session while we were loading - merge it in instead of
      // clobbering it (or letting its premature persist clobber the store).
      const writtenDuring = cache;
      const raced = writtenDuring != null && writtenDuring.length > 0;
      cache = raced ? mergeRosterOnHydration(loaded, writtenDuring) : loaded;
      if (raced || migratedLegacy) {
        await idbSave(cache.map(compactRecord));
      }
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* fine — the blob just lingers */
      }
      hydrated = true;
      emit();
    } catch {
      // IDB refused entirely — run on whatever localStorage still holds,
      // merged with any writes that raced the failed hydration.
      const legacy = readLegacyLocalStorage();
      const writtenDuring = cache;
      cache =
        legacy != null && writtenDuring != null && writtenDuring.length > 0
          ? mergeRosterOnHydration(legacy, writtenDuring)
          : (legacy ?? cache ?? []);
      hydrated = true;
      emit();
    } finally {
      hydrating = null;
    }
  })();
}

function readStore(): MyPokemonRecord[] {
  if (idbAvailable()) {
    ensureHydrated();
    return cache ?? [];
  }
  // Fallback path (tests / no-IDB WebViews): the original sync read.
  if (cache) return cache;
  return (cache = readLegacyLocalStorage() ?? []);
}

/** Single-flight background persist of whatever the cache holds NOW. */
let persistChain: Promise<boolean> = Promise.resolve(true);

function persistNow(): Promise<boolean> {
  if (!idbAvailable()) {
    try {
      if (typeof window === "undefined") return Promise.resolve(false);
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ records: (cache ?? []).map(compactRecord) }),
      );
      return Promise.resolve(true);
    } catch {
      return Promise.resolve(false);
    }
  }
  persistChain = persistChain
    .catch(() => false)
    .then(async () => {
      // Hydration-race fix (2026-08-30): a persist must never land while the
      // stored roster is still loading - mid-hydration the cache holds only
      // THIS session's writes, and saving it would overwrite the whole stored
      // roster with them. Wait for hydration to finish (its completion merges
      // racing writes into the cache), then save the merged truth. No
      // deadlock: ensureHydrated saves via idbSave directly, never through
      // this chain.
      if (hydrating) {
        try {
          await hydrating;
        } catch {
          /* hydration's own catch already recovered the cache */
        }
      }
      try {
        await idbSave((cache ?? []).map(compactRecord));
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[my-pokemon] persist failed", err);
        return false;
      }
    });
  return persistChain;
}

function writeStore(records: MyPokemonRecord[]): boolean {
  if (typeof window === "undefined") return false;
  const capped = records.slice(0, MAX_RECORDS);
  cache = capped;
  if (idbAvailable()) {
    // Make sure hydration is at least IN FLIGHT before the persist queues, so
    // the persist's hydration gate has a promise to wait on even for a write
    // path that never called readStore() first.
    ensureHydrated();
    // Optimistic for the scan path: the cache is authoritative this session;
    // a rare IDB failure is logged by persistNow. The bulk import path calls
    // persistNow() itself and AWAITS it, so a real storage refusal there is
    // reported to the user rather than assumed away.
    void persistNow();
    return true;
  }
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ records: capped.map(compactRecord) }),
    );
    return true;
  } catch {
    cache = null;
    return false;
  }
}

function emit(): void {
  // The sorted-list snapshot must die HERE, not in the subscriber: scan
  // writes land while the My Pokémon page is unmounted (the user is in
  // Pokémon GO), so no listener exists to invalidate it — the page then
  // mounted onto a stale list and new records never appeared (2026-08-20
  // device report).
  snapshotCache = null;
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(MY_POKEMON_EVENT));
  } catch {
    /* SSR / non-DOM runtime */
  }
}

// ── Reads ────────────────────────────────────────────────────────────────────

/** Every record, newest-updated first. */
export function listMyPokemon(): MyPokemonRecord[] {
  return [...readStore()].sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getMyPokemon(id: string): MyPokemonRecord | null {
  return readStore().find((r) => r.id === id) ?? null;
}

export function myPokemonCount(): number {
  return readStore().length;
}

/** IV sum as a 0-100 percent, or null while any IV is unread. */
export function ivPercent(
  r: Pick<MyPokemonRecord, "ivAtk" | "ivDef" | "ivHp">,
): number | null {
  if (r.ivAtk == null || r.ivDef == null || r.ivHp == null) return null;
  return Math.round(((r.ivAtk + r.ivDef + r.ivHp) / 45) * 1000) / 10;
}

/**
 * GO's appraisal stars for an IV total (2026-09-29, for the record page's
 * IV editor): 37+ of 45 is 3★ (82.2%), 30+ is 2★ (66.7%), 23+ is 1★
 * (51.1%), below is 0★. The 4★ hundo badge is a display rule, not stored.
 */
export function appraisalStarsForIvs(atk: number, def: number, hp: number): number {
  const total = atk + def + hp;
  return total >= 37 ? 3 : total >= 30 ? 2 : total >= 23 ? 1 : 0;
}

/**
 * Manual IV edit (2026-09-29 owner ask: drag the dots on the record page's
 * bars, then save). Whole numbers 0 to 15; the appraisal stars follow the
 * new total. The caller runs the CP curve check and passes `clearCpHp`
 * when the recorded CP and HP cannot be reached with these IVs: both go
 * blank (and cpDerived off) for the trainer to update.
 */
export function setRecordIvs(
  id: string,
  ivs: { ivAtk: number; ivDef: number; ivHp: number },
  opts: { clearCpHp?: boolean } = {},
): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const clamp = (n: number) => Math.max(0, Math.min(15, Math.round(n)));
  const ivAtk = clamp(ivs.ivAtk);
  const ivDef = clamp(ivs.ivDef);
  const ivHp = clamp(ivs.ivHp);
  const same = existing.ivAtk === ivAtk && existing.ivDef === ivDef && existing.ivHp === ivHp;
  if (same && !opts.clearCpHp) return true;
  const rec: MyPokemonRecord = {
    ...existing,
    ivAtk,
    ivDef,
    ivHp,
    appraisalStars: appraisalStarsForIvs(ivAtk, ivDef, ivHp),
    ...(opts.clearCpHp ? { cp: null, hpMax: null, cpDerived: false } : {}),
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

export function isPerfect(
  r: Pick<MyPokemonRecord, "ivAtk" | "ivDef" | "ivHp">,
): boolean {
  return r.ivAtk === 15 && r.ivDef === 15 && r.ivHp === 15;
}

// ── Identity + merge ─────────────────────────────────────────────────────────

/**
 * Find the stored record this observation is another look at.
 *
 * The identity core of a caught Pokémon, as visible on its page, is
 * (species, CP, max HP): two views of one Pokémon always agree on those, and
 * two DIFFERENT Pokémon of the same species rarely collide on both CP and HP.
 * A field one side hasn't read yet never blocks a match — but at least ONE of
 * CP / max HP must corroborate, so "same species" alone can never merge two
 * different Pokémon. When both sides carry a nickname they must agree.
 */
export function findMatchingRecord(
  obs: MyPokemonObservation,
  records: MyPokemonRecord[] = readStore(),
): MyPokemonRecord | null {
  // Nicknames compare through nicknameCompareKey (2026-08-31): OCR flattens
  // styled glyphs ("Zubat²²²" reads back as "Zubat222"), so a re-scan of a
  // glyph-named Pokémon must still find its own record rather than minting a
  // duplicate — decoded-equal is the same name.
  const nameKey = (s: string | null | undefined) => nicknameCompareKey(s);
  const candidates = records.filter((r) => {
    if (obs.speciesId != null && r.speciesId != null) {
      if (obs.speciesId !== r.speciesId) return false;
    } else if (obs.speciesId == null && r.speciesId == null) {
      // Neither side resolved a species — require the printed name to agree.
      const a = nameKey(obs.nickname);
      const b = nameKey(r.nickname);
      if (!a || a !== b) return false;
    } else if (obs.speciesId == null) {
      // The observation is unresolved but the record is not: a nickname match
      // ties them (the nicknamed detail view of a record the appraisal named).
      const a = nameKey(obs.nickname);
      const b = nameKey(r.nickname);
      if (!a || a !== b) return false;
    } else if (r.speciesId == null) {
      const a = nameKey(obs.nickname);
      const b = nameKey(r.nickname);
      if (!a || a !== b) return false;
    }
    let corroborated = 0;
    const sameIvs =
      obs.iv != null &&
      r.ivAtk != null &&
      r.ivDef != null &&
      r.ivHp != null &&
      obs.iv.atk === r.ivAtk &&
      obs.iv.def === r.ivDef &&
      obs.iv.hp === r.ivHp;
    if (obs.cp != null && r.cp != null) {
      if (obs.cp !== r.cp) {
        // A CALCULATED CP (cpFromHp, 2026-09-20) is the record's best guess
        // from its HP and IVs, not a read. A frame that reads the real CP
        // corrects it, provided the frame agrees on what the guess was made
        // from: the same max HP, or (HP off screen) the same IV bars.
        const guessCorrected =
          !!r.cpDerived &&
          ((obs.hpMax != null && r.hpMax != null && obs.hpMax === r.hpMax) ||
            (obs.hpMax == null && sameIvs));
        if (!guessCorrected) return false;
        // What the guess was made from agreed, and that is the corroboration.
        corroborated += 1;
      } else {
        corroborated += 1;
      }
    }
    if (obs.hpMax != null && r.hpMax != null) {
      if (obs.hpMax !== r.hpMax) return false;
      corroborated += 1;
    }
    // A max HP seen earlier in the dwell, before the overlay hid it, still
    // rules out a record with a different one (2026-10-07: two Pokémon of
    // one species and one CP, the second only ever seen with its appraisal
    // open, were folded into one record).
    if (obs.hpVeto != null && r.hpMax != null && obs.hpVeto !== r.hpMax) return false;
    // AN APPRAISAL FRAME WITH NOTHING ELSE (2026-09-20 device report: four
    // records of one Electrode from one live scan). The overlay hides the HP
    // and can hide the CP, so a frame that read only the IV bars had nothing
    // to corroborate on and minted a record every time. The bars are the
    // one read the overlay always gives; when they equal a stored triple
    // of the same species, and the frame has no CP or HP to disagree, that
    // is the Pokémon being appraised. Two real Pokémon of one species with
    // one triple are rare (1 in 4096 wild), and Box Cleanup still lists them.
    if (obs.cp == null && obs.hpMax == null && sameIvs) corroborated += 1;
    if (obs.nickname && r.nickname && nameKey(obs.nickname) !== nameKey(r.nickname)) return false;
    // IV CONFLICT REJECTS (2026-08-20 device pass): flipping through a batch
    // of the SAME species, the new one's CP is often occluded by the appraisal
    // seal while its max HP happens to equal the previous one's — hpMax alone
    // then corroborated a cross-match and the HUD showed the PRIOR Pokémon's
    // derived stats ("stuck GL/UL"). The IV bars are the one thing the
    // appraisal always shows, so two different triples are two different
    // Pokémon, full stop.
    // The bars count here even before they are confirmed (ivVeto): a triple
    // that differs is a different Pokémon whether or not it is settled enough
    // to write yet.
    const vetoIv = obs.iv ?? obs.ivVeto ?? null;
    if (
      vetoIv != null &&
      r.ivAtk != null &&
      r.ivDef != null &&
      r.ivHp != null &&
      (vetoIv.atk !== r.ivAtk || vetoIv.def !== r.ivDef || vetoIv.hp !== r.ivHp)
    ) {
      return false;
    }
    return corroborated >= 1;
  });
  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => b.updatedAt - a.updatedAt)[0];
}

function unionStrings(a: string[], b: string[]): string[] {
  const out = [...a];
  for (const s of b) if (!out.includes(s)) out.push(s);
  return out;
}

/** Merge an observation into a record. Non-null incoming wins; a frame that
 * didn't see a field never blanks it. Returns whether anything changed. */
function mergeInto(rec: MyPokemonRecord, obs: MyPokemonObservation): boolean {
  let changed = false;
  const set = <K extends keyof MyPokemonRecord>(
    key: K,
    value: MyPokemonRecord[K] | null | undefined,
  ) => {
    if (value == null) return;
    if (rec[key] === value) return;
    rec[key] = value;
    changed = true;
  };
  set("speciesId", obs.speciesId);
  set("speciesName", obs.speciesName);
  set("nickname", obs.nickname);
  if (obs.cp != null) {
    set("cp", obs.cp);
    // A read CP replaces a calculated one and is no longer a guess.
    if (rec.cpDerived) {
      rec.cpDerived = false;
      changed = true;
    }
  }
  set("hpMax", obs.hpMax);
  if (obs.iv) {
    set("ivAtk", obs.iv.atk);
    set("ivDef", obs.iv.def);
    set("ivHp", obs.iv.hp);
  }
  set("appraisalStars", obs.appraisalStars);
  set("gender", obs.gender ?? null);
  set("weightKg", obs.weightKg);
  set("heightM", obs.heightM);
  set("sizeClass", obs.sizeClass);
  set("caughtDateRaw", obs.caughtDateRaw);
  set("caughtDateIso", obs.caughtDateIso);
  set("caughtLocation", obs.caughtLocation);
  if (obs.isLucky) set("isLucky", true);
  if (obs.isShiny) set("isShiny", true);
  if (obs.isShadow) set("isShadow", true);
  if (obs.isPurified) set("isPurified", true);
  // Raise-only, like every other flag here: an observation can prove a Max
  // band was printed, never that one was absent (a shot cropped above the band
  // reads the same as a Pokémon that has none).
  if (obs.isDynamax) set("isDynamax", true);
  if (obs.isGmax) set("isGmax", true);
  if (obs.isMega) set("isMega", true);
  // The Mega Level circles: a filled circle is proof of a level reached, so
  // the record's level only ever rises from a read, and a read at Base or
  // above marks the Pokémon as Mega Evolved.
  const speciesForMega = obs.speciesId ?? rec.speciesId;
  if (obs.megaReads && obs.megaReads.length > 0 && speciesForMega != null) {
    for (const read of obs.megaReads) {
      const level = read.level;
      if (level !== 1 && level !== 2 && level !== 3 && level !== 4) continue;
      const key = megaFormKeyForLabel(speciesForMega, read.formLabel);
      if (!key) continue;
      const cur = rec.megaLevels?.[key] ?? 0;
      if (level > cur) {
        rec.megaLevels = { ...(rec.megaLevels ?? {}), [key]: level };
        changed = true;
      }
      if (!rec.isMega) {
        rec.isMega = true;
        changed = true;
      }
    }
  }
  // Max Moves: the ribbon proves the ability, a tile's level is a level
  // reached, so both only ever rise. The attack NAME follows the latest
  // read: a Fast TM changes it in GO without resetting the level.
  if (obs.maxMoves) {
    const read = obs.maxMoves;
    if (read.hasMaxMoves && !rec.hasMaxMoves) {
      rec.hasMaxMoves = true;
      changed = true;
    }
    const cur: MyPokemonMaxMoves = { ...(rec.maxMoves ?? {}) };
    let touched = false;
    if (read.attack) {
      const level = Math.max(read.attack.level, cur.attack?.level ?? 0) as 1 | 2 | 3;
      const name = read.attack.name ?? cur.attack?.name ?? null;
      if (!cur.attack || cur.attack.level !== level || cur.attack.name !== name) {
        cur.attack = { name, level };
        touched = true;
      }
    }
    for (const slot of ["guard", "spirit"] as const) {
      const level = read[slot];
      if (level == null) continue;
      const prev = cur[slot];
      if (prev == null || level > prev) {
        cur[slot] = level;
        touched = true;
      }
    }
    if (touched) {
      rec.maxMoves = cur;
      rec.hasMaxMoves = true;
      changed = true;
    }
  }
  set("form", obs.form);
  // A CSV row's resolved form (csvFormResolve.ts) lands BEFORE the typing
  // below is derived, since a regional form's types follow its formId.
  set("formId", obs.formId);
  // The typing comes from the species table, never the read (2026-09-20
  // owner direction, after Medicham lost Psychic to a one-pill appraisal
  // frame). The read only matters for a regional form, which has no table
  // yet; see myPokemonTypes.
  {
    const read = obs.types && obs.types.length > 0 ? obs.types : rec.types;
    const next = myPokemonTypes({ speciesId: rec.speciesId, formId: rec.formId, types: read });
    if (JSON.stringify(next) !== JSON.stringify(rec.types)) {
      rec.types = [...next];
      changed = true;
    }
  }
  if (obs.moves && obs.moves.length > 0) {
    // The moveset is read whole from one view; a longer/newer read replaces.
    if (
      obs.moves.length >= rec.moves.length &&
      JSON.stringify(obs.moves) !== JSON.stringify(rec.moves)
    ) {
      rec.moves = obs.moves.map((m) => ({ ...m }));
      changed = true;
    }
  }
  if (obs.tags && obs.tags.length > 0) {
    const next = unionStrings(rec.tags, obs.tags);
    if (next.length !== rec.tags.length) {
      rec.tags = next;
      changed = true;
    }
  }
  if (obs.provenance && obs.provenance.length > rec.provenance.length) {
    rec.provenance = obs.provenance.map((p) => ({ ...p }));
    changed = true;
  }
  if (fillDerivedCp(rec)) changed = true;
  return changed;
}

/**
 * The CP the record's own IVs and max HP name, written in while the CP is
 * blank or still a calculation (2026-09-20 owner ask: the appraisal overlay
 * hides the CP banner on many pages, and the maths is exact for most wild
 * catches). Never touches a read or typed CP. Returns whether it wrote.
 */
function fillDerivedCp(rec: MyPokemonRecord): boolean {
  if (rec.cp != null && !rec.cpDerived) return false;
  if (rec.cpManual != null) return false;
  const c = cpFromHp(rec);
  if (!c) return false;
  if (rec.cp === c.cp && rec.cpDerived) return false;
  rec.cp = c.cp;
  rec.cpDerived = true;
  return true;
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Merge a scanner observation into the database: update the record it
 * matches, or create a new one. This is the ONLY scan write path.
 */
export function upsertScannedPokemon(obs: MyPokemonObservation): UpsertResult {
  const records = [...readStore()];
  const now = Date.now();
  const existing = findMatchingRecord(obs, records);
  if (existing) {
    const rec = { ...existing, moves: [...existing.moves], tags: [...existing.tags], types: [...existing.types], provenance: [...existing.provenance] };
    const changed = mergeInto(rec, obs);
    if (!changed) return { record: existing, created: false, changed: false };
    rec.updatedAt = now;
    rec.scannedAt = now;
    const next = records.map((r) => (r.id === rec.id ? rec : r));
    if (!writeStore(next)) return { record: existing, created: false, changed: false };
    emit();
    return { record: rec, created: false, changed: true };
  }
  const rec: MyPokemonRecord = {
    id: newMyPokemonId(),
    speciesId: null,
    speciesName: null,
    nickname: null,
    cp: null,
    hpMax: null,
    cpManual: null,
    ivAtk: null,
    ivDef: null,
    ivHp: null,
    appraisalStars: null,
    moves: [],
    gender: null,
    weightKg: null,
    heightM: null,
    sizeClass: null,
    types: [],
    isLucky: false,
    isShiny: false,
    isShadow: false,
    isPurified: false,
    tags: [],
    caughtDateRaw: null,
    caughtDateIso: null,
    caughtLocation: null,
    provenance: [],
    form: null,
    formId: null,
    source: "scan",
    addedVia: obs.addedVia,
    createdAt: now,
    updatedAt: now,
    scannedAt: now,
  };
  mergeInto(rec, obs);
  const next = [rec, ...records];
  if (!writeStore(next)) return { record: rec, created: false, changed: false };
  emit();
  return { record: rec, created: true, changed: true };
}

export interface ImportSummary {
  created: number;
  merged: number;
  skipped: number;
  /** True when the cap truncated the import. */
  truncated: boolean;
  /** True when the records parsed but STORAGE rejected the write (quota). */
  storageFailed: boolean;
}

/**
 * Bulk import (the Poké Genie CSV path): one identity pass over a keyed map
 * instead of findMatchingRecord per row — a 3,000-row CSV against a 3,000-row
 * store must not be O(n²) — then ONE storage write and ONE change event.
 *
 * Identity here is EXACT (species + CP + maxHP + IV triple): an import is a
 * snapshot of known-complete rows, so the scanner's partial-observation
 * matching rules don't apply. A row matching an existing record merges into
 * it (filling gaps, same non-null-wins rule); anything else is created.
 */
export async function importMyPokemonRecords(
  incoming: MyPokemonObservation[],
  onProgress?: (done: number, total: number) => void,
): Promise<ImportSummary> {
  const before = readStore();
  const records = [...before];
  const now = Date.now();
  const keyOf = (r: {
    speciesId?: number | null;
    cp?: number | null;
    hpMax?: number | null;
  }, iv: { atk: number | null; def: number | null; hp: number | null }) =>
    `${r.speciesId ?? ""}|${r.cp ?? ""}|${r.hpMax ?? ""}|${iv.atk ?? ""}|${iv.def ?? ""}|${iv.hp ?? ""}`;

  const existingByKey = new Map<string, MyPokemonRecord>();
  for (const r of records) {
    existingByKey.set(keyOf(r, { atk: r.ivAtk, def: r.ivDef, hp: r.ivHp }), r);
  }

  let created = 0;
  let merged = 0;
  let skipped = 0;
  let processed = 0;
  for (const obs of incoming) {
    // Yield the main thread every few hundred rows so the progress bar (and
    // everything else on screen) keeps painting through a multi-thousand-row
    // import — the merge is cheap but a single synchronous pass still blocks.
    processed += 1;
    if (processed % 250 === 0) {
      onProgress?.(processed, incoming.length);
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
    if (obs.speciesId == null || (obs.cp == null && obs.hpMax == null)) {
      skipped += 1;
      continue;
    }
    const key = keyOf(obs, {
      atk: obs.iv?.atk ?? null,
      def: obs.iv?.def ?? null,
      hp: obs.iv?.hp ?? null,
    });
    const existing = existingByKey.get(key);
    if (existing) {
      if (mergeInto(existing, obs)) {
        existing.updatedAt = now;
        merged += 1;
      } else {
        skipped += 1;
      }
      continue;
    }
    if (records.length >= MAX_RECORDS) {
      skipped += 1;
      continue;
    }
    const rec: MyPokemonRecord = {
      id: newMyPokemonId(),
      speciesId: null,
      speciesName: null,
      nickname: null,
      cp: null,
      hpMax: null,
      cpManual: null,
      ivAtk: null,
      ivDef: null,
      ivHp: null,
      appraisalStars: null,
      moves: [],
      gender: null,
      weightKg: null,
      heightM: null,
      sizeClass: null,
      types: [],
      isLucky: false,
      isShiny: false,
      isShadow: false,
      isPurified: false,
      tags: [],
      caughtDateRaw: null,
      caughtDateIso: null,
      caughtLocation: null,
      provenance: [],
      form: null,
      formId: null,
      source: "import",
      addedVia: "import",
      createdAt: now,
      updatedAt: now,
    };
    mergeInto(rec, obs);
    records.push(rec);
    existingByKey.set(key, rec);
    created += 1;
  }

  onProgress?.(incoming.length, incoming.length);
  const truncated = records.length > MAX_RECORDS;
  if (created + merged > 0) {
    cache = records.slice(0, MAX_RECORDS);
    // AWAIT the persist: a bulk import must report a storage refusal, not
    // assume it away. (The scan path stays optimistic — see writeStore.)
    const ok = await persistNow();
    if (!ok) {
      // Roll the cache back so the page never shows records that will
      // vanish on the next launch.
      cache = before;
      return {
        created: 0,
        merged: 0,
        skipped: 0,
        truncated: false,
        storageFailed: true,
      };
    }
    emit();
  }
  return { created, merged, skipped, truncated, storageFailed: false };
}

/**
 * Merge an observation into ONE KNOWN record, by id — the dwell-continuity
 * write. Deep pages (Zamazenta) scroll the name and CP off-screen before the
 * moveset scrolls on, so those frames carry no identity to match; the live
 * scan latch knows which record this dwell already committed and attaches
 * the payload directly.
 */
/** Read-only lookup by id (the live scan's dwell latch, 2026-09-03). */
export function findRecordById(id: string): MyPokemonRecord | null {
  return readStore().find((r) => r.id === id) ?? null;
}

export function mergeIntoRecordById(
  id: string,
  obs: MyPokemonObservation,
  /** `scanned: false` for a merge that is not a scan (Box Cleanup, Update
   *  all), so the keeper holds its place in the scanned order. */
  opts?: { scanned?: boolean },
): UpsertResult | null {
  const records = [...readStore()];
  const existing = records.find((r) => r.id === id);
  if (!existing) return null;
  // Identity guard (2026-09-04): a by-id merge is dwell continuity, not a
  // match, so an observation that names a DIFFERENT species is a different
  // Pokémon and must never land in this record.
  if (
    obs.speciesId != null &&
    existing.speciesId != null &&
    obs.speciesId !== existing.speciesId
  ) {
    return null;
  }
  const rec = {
    ...existing,
    moves: [...existing.moves],
    tags: [...existing.tags],
    types: [...existing.types],
    provenance: [...existing.provenance],
  };
  const changed = mergeInto(rec, obs);
  if (!changed) return { record: existing, created: false, changed: false };
  rec.updatedAt = Date.now();
  // Dwell continuity and a screenshot applied to this record are scans.
  if (opts?.scanned !== false) rec.scannedAt = rec.updatedAt;
  const next = records.map((r) => (r.id === rec.id ? rec : r));
  if (!writeStore(next)) return { record: existing, created: false, changed: false };
  emit();
  return { record: rec, created: false, changed: true };
}

/**
 * Set a record's moveset outright — the detail page's tap-to-pick move
 * selector (2026-08-21). Unlike mergeInto (which only accepts a LONGER
 * moveset, the right rule for OCR reads), a manual pick may also shrink or
 * clear, so this writes exactly what it is given.
 */
export function setRecordMoves(id: string, moves: MyPokemonMoveRecord[]): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const rec: MyPokemonRecord = {
    ...existing,
    moves: moves.map((m) => ({ ...m })),
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * Set a record's variant flags outright — the detail page's shiny / lucky /
 * shadow / purified toggles (2026-08-24). The scan merge path only ever
 * raises a flag (an observation can't prove absence), but a manual toggle
 * must also LOWER one, so this writes exactly what it is given. GO-legality
 * exclusions (shadow vs purified/lucky) are the caller's job — the store
 * stays an exact writer, same contract as setRecordMoves.
 */
/** The record's per-form Mega Levels, dropping anything that is not 1..4. */
function readMegaLevels(v: unknown): Record<string, 1 | 2 | 3 | 4> | undefined {
  if (!v || typeof v !== "object") return undefined;
  const out: Record<string, 1 | 2 | 3 | 4> = {};
  for (const [k, lv] of Object.entries(v as Record<string, unknown>)) {
    if (lv === 1 || lv === 2 || lv === 3 || lv === 4) out[k] = lv;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

const MAX_SLOT_LEVELS = [0, 1, 2, 3] as const;
function isMaxSlotLevel(v: unknown): v is 0 | 1 | 2 | 3 {
  return (MAX_SLOT_LEVELS as readonly unknown[]).includes(v);
}

/** The record's Max Moves, dropping any slot that is not a level 0..3. */
function readMaxMoves(v: unknown): MyPokemonMaxMoves | undefined {
  if (!v || typeof v !== "object") return undefined;
  const raw = v as Record<string, unknown>;
  const out: MyPokemonMaxMoves = {};
  const attack = raw.attack;
  if (attack && typeof attack === "object") {
    const a = attack as Record<string, unknown>;
    if (a.level === 1 || a.level === 2 || a.level === 3) {
      out.attack = { name: typeof a.name === "string" ? a.name : null, level: a.level };
    }
  }
  if (isMaxSlotLevel(raw.guard)) out.guard = raw.guard;
  if (isMaxSlotLevel(raw.spirit)) out.spirit = raw.spirit;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Hand-set whether THIS Pokémon uses Max Moves (2026-09-04): the Dynamax
 * button on a Crowned Zacian / Zamazenta or an Eternatus, which use Max
 * Moves without being in the Dynamax dex. Clearing it also drops the slots.
 */
export function setRecordHasMaxMoves(id: string, on: boolean): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  if (!!existing.hasMaxMoves === on && (on || !existing.maxMoves)) return true;
  const rec: MyPokemonRecord = {
    ...existing,
    hasMaxMoves: on,
    maxMoves: on ? existing.maxMoves : undefined,
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * Set (or clear with null) one of THIS Pokémon's Max Move slots by hand
 * (2026-09-04). An exact writer like setRecordMoves: the tile the trainer
 * tapped is the truth, whatever a scan read before. Setting any slot marks
 * the record as having Max Moves; the attack slot keeps its name unless one
 * is given.
 */
export function setRecordMaxMove(
  id: string,
  slot: "attack" | "guard" | "spirit",
  level: 0 | 1 | 2 | 3 | null,
  name?: string | null,
): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const next: MyPokemonMaxMoves = { ...(existing.maxMoves ?? {}) };
  if (slot === "attack") {
    if (level == null || level === 0) delete next.attack;
    else next.attack = { name: name !== undefined ? name : (next.attack?.name ?? null), level };
  } else if (level == null) {
    delete next[slot];
  } else {
    next[slot] = level;
  }
  const rec: MyPokemonRecord = {
    ...existing,
    maxMoves: Object.keys(next).length > 0 ? next : undefined,
    hasMaxMoves: existing.hasMaxMoves || level != null,
    updatedAt: Date.now(),
  };
  const nextRecords = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(nextRecords)) return false;
  emit();
  return true;
}

/**
 * Set (or clear with null) THIS Pokémon's Mega Level for one Mega form
 * (2026-09-03). Marks the record as Mega Evolved too when a level is set:
 * a level is only reached by evolving.
 */
export function setRecordMegaLevel(
  id: string,
  formKey: string,
  level: 1 | 2 | 3 | 4 | null,
): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const cur = existing.megaLevels ?? {};
  if ((cur[formKey] ?? null) === level) return true;
  const nextLevels = { ...cur };
  if (level == null) delete nextLevels[formKey];
  else nextLevels[formKey] = level;
  const rec: MyPokemonRecord = {
    ...existing,
    megaLevels: Object.keys(nextLevels).length > 0 ? nextLevels : undefined,
    isMega: existing.isMega || level != null,
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

export function setRecordVariantFlags(
  id: string,
  flags: Partial<
    Pick<
      MyPokemonRecord,
      | "isShiny"
      | "isLucky"
      | "isShadow"
      | "isPurified"
      | "isBestBuddy"
      | "isMega"
      | "isDynamax"
      | "isGmax"
    >
  >,
): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const rec: MyPokemonRecord = { ...existing, ...flags, updatedAt: Date.now() };
  // The Best Buddy flag and the buddy level agree both ways: flagging Best
  // sets the level, un-flagging it drops a "best" level (a lower level the
  // trainer picked stands).
  if (flags.isBestBuddy !== undefined) {
    if (flags.isBestBuddy) rec.buddyLevel = "best";
    else if (existing.buddyLevel === "best") rec.buddyLevel = null;
  }
  if (
    rec.isShiny === existing.isShiny &&
    rec.isLucky === existing.isLucky &&
    rec.isShadow === existing.isShadow &&
    rec.isPurified === existing.isPurified &&
    !!rec.isBestBuddy === !!existing.isBestBuddy &&
    (rec.buddyLevel ?? null) === (existing.buddyLevel ?? null) &&
    !!rec.isMega === !!existing.isMega &&
    !!rec.isDynamax === !!existing.isDynamax &&
    !!rec.isGmax === !!existing.isGmax
  ) {
    return true;
  }
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * The buddy level (2026-09-20 owner ask): Good, Great, Ultra, Best, or null
 * for "not a buddy". Best Buddy keeps its own flag (the level-51 cap) and
 * follows the level.
 */
export function setRecordBuddyLevel(id: string, level: MyPokemonBuddyLevel | null): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  if ((existing.buddyLevel ?? null) === level && !!existing.isBestBuddy === (level === "best")) return true;
  const rec: MyPokemonRecord = {
    ...existing,
    buddyLevel: level,
    isBestBuddy: level === "best",
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/** The record's tag pills, replaced whole (2026-09-29: swipe a tag away on the record page, Undo puts it back). */
export function setRecordTags(id: string, tags: readonly string[]): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const next = [...new Set(tags.map((t) => t.trim()).filter((t) => t.length > 0))];
  if (next.length === existing.tags.length && next.every((t, i) => t === existing.tags[i])) return true;
  const rec: MyPokemonRecord = { ...existing, tags: next, updatedAt: Date.now() };
  if (!writeStore(records.map((r) => (r.id === id ? rec : r)))) return false;
  emit();
  return true;
}

/** The star (2026-09-29): protected from deletion. */
export function setRecordFavourite(id: string, on: boolean): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  if (!!existing.isFavourite === on) return true;
  const rec: MyPokemonRecord = { ...existing, isFavourite: on, updatedAt: Date.now() };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * Manual CP edit — the detail page's power-up control (2026-08-24). Writes
 * the CP exactly; when the caller resolved the matching max HP from the CP
 * curve (resolveManualCp / manualCpLadder) it rides along, so the record
 * stays curve-consistent and the sanitizer has nothing to clear.
 */
export function setRecordCp(
  id: string,
  cp: number,
  hpMax?: number,
  /** `manual`: the trainer typed this CP (and HP), so it stands even off the IV curve.
   *  `derived`: the CP was calculated from the HP and IVs (cpFromHp); a read may correct it. */
  opts?: { manual?: boolean; derived?: boolean },
): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const cpManual = opts?.manual ? cp : existing.cpManual === cp ? existing.cpManual : null;
  // A CP the trainer set is a statement, not a calculation.
  const cpDerived = opts?.derived ? true : false;
  if (
    existing.cp === cp &&
    (hpMax === undefined || existing.hpMax === hpMax) &&
    existing.cpManual === cpManual &&
    !!existing.cpDerived === cpDerived
  ) {
    return true;
  }
  const rec: MyPokemonRecord = {
    ...existing,
    cp,
    cpManual,
    cpDerived,
    ...(hpMax !== undefined ? { hpMax } : {}),
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * "Evolved it?" (2026-09-15 owner ask): the trainer evolved this Pokémon in
 * GO and presses the button instead of deleting and rescanning. IVs, level,
 * nickname, flags, tags and provenance carry through, as in GO; species,
 * name, types and form move to the evolved stage (a regional or costume
 * line keeps its form); CP and max HP are recomputed at the SAME level from
 * the new base stats when the level is known, else cleared for the CP
 * editor; the moveset is cleared (an evolution rolls new moves); a locked
 * PvP target that no longer sits on the new chain is dropped. What was
 * overwritten is kept in `evolvedFrom` so undoEvolveRecord can put it back.
 */
export function evolveRecord(
  id: string,
  targetSpeciesId: number,
  level: number | null,
): MyPokemonRecord | null {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing || existing.speciesId == null) return null;
  if (!evolutionChainFor(existing.speciesId).includes(targetSpeciesId)) return null;
  if (!formCanEvolveTo(existing.formId, targetSpeciesId)) return null;
  const targetFormId = evolvedFormIdFor(existing.formId, targetSpeciesId);
  const base = baseStatsForRecord({ speciesId: targetSpeciesId, formId: targetFormId, gender: existing.gender });
  const ivs =
    existing.ivAtk != null && existing.ivDef != null && existing.ivHp != null
      ? { atk: existing.ivAtk, def: existing.ivDef, sta: existing.ivHp }
      : null;
  const cp = base && ivs && level != null ? computeCp(base, ivs, level) : null;
  const hpMax = base && ivs && level != null ? computeHp(base, ivs.sta, level) : null;
  const chainAhead = new Set([targetSpeciesId, ...evolutionChainFor(targetSpeciesId)]);
  const snapshot: MyPokemonEvolutionSnapshot = {
    speciesId: existing.speciesId,
    speciesName: existing.speciesName,
    formId: existing.formId,
    cp: existing.cp,
    hpMax: existing.hpMax,
    cpManual: existing.cpManual ?? null,
    moves: [...existing.moves],
    types: [...existing.types],
    pvpTarget: existing.pvpTarget ?? null,
    isMega: !!existing.isMega,
    ...(existing.megaLevels ? { megaLevels: { ...existing.megaLevels } } : {}),
    at: Date.now(),
  };
  const { megaLevels: _dropMega, ...rest } = existing;
  void _dropMega;
  const rec: MyPokemonRecord = {
    ...rest,
    speciesId: targetSpeciesId,
    speciesName: speciesNameFor(targetSpeciesId),
    formId: targetFormId,
    cp,
    hpMax,
    cpManual: null,
    moves: [],
    types: typesFor(targetSpeciesId),
    isMega: false,
    pvpTarget:
      existing.pvpTarget && chainAhead.has(existing.pvpTarget.formSpeciesId) ? existing.pvpTarget : null,
    evolvedFrom: snapshot,
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return null;
  emit();
  return rec;
}

/** Put an evolution back (the Undo under the button). Null when there is nothing to undo. */
export function undoEvolveRecord(id: string): MyPokemonRecord | null {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  const snap = existing?.evolvedFrom;
  if (!existing || !snap) return null;
  const rec: MyPokemonRecord = {
    ...existing,
    speciesId: snap.speciesId,
    speciesName: snap.speciesName,
    formId: snap.formId,
    cp: snap.cp,
    hpMax: snap.hpMax,
    cpManual: snap.cpManual,
    moves: [...snap.moves],
    types: [...snap.types],
    pvpTarget: snap.pvpTarget,
    isMega: snap.isMega,
    ...(snap.megaLevels ? { megaLevels: { ...snap.megaLevels } } : {}),
    evolvedFrom: null,
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return null;
  emit();
  return rec;
}

/**
 * Manual nickname edit — the detail page's name pencil (2026-08-31 ask).
 * The scanner's name OCR can mangle glyph-styled nicknames (superscripts,
 * circled digits), so the hand edit is the correction path. An empty or
 * whitespace-only value clears the nickname (display falls back to the
 * species name).
 */
export function setRecordNickname(id: string, nickname: string | null): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const clean = nickname != null && nickname.trim().length > 0 ? nickname.trim() : null;
  if (existing.nickname === clean) return true;
  const rec: MyPokemonRecord = { ...existing, nickname: clean, updatedAt: Date.now() };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * Set a record's size class by hand (2026-09-10 owner ask): the scanner only
 * reads the badge off a detail-page screenshot, so a Pokémon added any other
 * way had no route into the XXL / XXS dexes. null clears it.
 */
export function setRecordSizeClass(id: string, sizeClass: MyPokemonSizeClass): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  if (existing.sizeClass === sizeClass) return true;
  const rec: MyPokemonRecord = { ...existing, sizeClass, updatedAt: Date.now() };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/** Set a record's canonical form ("geodude-alola"); null returns it to the
 * base form. The detail page's regional-form picker (2026-08-24). */
export function setRecordFormId(id: string, formId: string | null): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  if (existing.formId === formId) return true;
  // A form change re-derives the typing (a regional form keeps a full read
  // of its own page; a base form is the species table).
  const rec: MyPokemonRecord = {
    ...existing,
    formId,
    types: myPokemonTypes({ speciesId: existing.speciesId, formId, types: existing.types }),
    updatedAt: Date.now(),
  };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/**
 * Lock (or clear, with null) the record's PvP power-up target — the form ×
 * league cell tapped in the detail page's matrix (2026-08-26).
 */
export function setRecordPvpTarget(
  id: string,
  target: { formSpeciesId: number; league: "great" | "ultra" | "master" } | null,
): boolean {
  const records = readStore();
  const existing = records.find((r) => r.id === id);
  if (!existing) return false;
  const same =
    (existing.pvpTarget ?? null) === null && target === null
      ? true
      : existing.pvpTarget != null &&
        target != null &&
        existing.pvpTarget.formSpeciesId === target.formSpeciesId &&
        existing.pvpTarget.league === target.league;
  if (same) return true;
  const rec: MyPokemonRecord = { ...existing, pvpTarget: target, updatedAt: Date.now() };
  const next = records.map((r) => (r.id === id ? rec : r));
  if (!writeStore(next)) return false;
  emit();
  return true;
}

/** Replace the whole record set (the sanitizer's repair write). */
export function replaceMyPokemonRecords(records: MyPokemonRecord[]): void {
  if (writeStore(records)) emit();
}

/** Delete one record (the page's per-row remove). */
export function deleteMyPokemon(id: string): boolean {
  const records = readStore();
  const next = records.filter((r) => r.id !== id);
  if (next.length === records.length) return false;
  if (!writeStore(next)) return false;
  emit();
  return true;
}

// ── Undoable deletion ────────────────────────────────────────────────────────
// Swipe-to-delete has no confirmation dialog, so the safety net moved to the
// other side of the action: the removed records sit in this buffer and an
// UNDO control on the list page can put them back. One buffer, latest
// deletion only, shared by the list swipe, the bulk select and the detail
// page's remove (which navigates back to the list, where the control shows).

export interface MyPokemonDeletion {
  records: MyPokemonRecord[];
  at: number;
}

let lastDeletion: MyPokemonDeletion | null = null;

/**
 * Delete records by id, keeping them undoable. Returns how many went.
 * A STARRED record never goes this way (2026-09-29): the star is the
 * trainer's "hands off", and this is the one door every user-facing delete
 * uses (swipe, select-mode, the record page, Box Cleanup, Update all), so
 * the guard lives here rather than in each caller. `force` is for a caller
 * that has already had the trainer unstar.
 */
export function deleteMyPokemonWithUndo(ids: string[], opts: { force?: boolean } = {}): number {
  const asked = new Set(ids);
  const records = readStore();
  const removed = records.filter((r) => asked.has(r.id) && (opts.force || !r.isFavourite));
  if (removed.length === 0) return 0;
  const going = new Set(removed.map((r) => r.id));
  const next = records.filter((r) => !going.has(r.id));
  if (!writeStore(next)) return 0;
  lastDeletion = { records: removed, at: Date.now() };
  emit();
  return removed.length;
}

/** The latest undoable deletion, or null once undone/never happened. */
export function peekMyPokemonDeletion(): MyPokemonDeletion | null {
  return lastDeletion;
}

/** Put the latest deletion back. Returns how many records were restored. */
export function undoMyPokemonDeletion(): number {
  if (!lastDeletion) return 0;
  const buffered = lastDeletion.records;
  lastDeletion = null;
  const records = readStore();
  const present = new Set(records.map((r) => r.id));
  // updatedAt bumps to NOW on restore: the undo is a fresh statement by the
  // trainer, and the cloud backup adjudicates by device time — restoring with
  // the record's old stamp would lose LWW to the deletion tombstone the
  // syncer already pushed, resurrecting the delete on the next pull.
  const now = Date.now();
  const restore = buffered
    .filter((r) => !present.has(r.id))
    .map((r) => ({ ...r, updatedAt: now }));
  if (restore.length === 0) return 0;
  if (!writeStore([...records, ...restore])) return 0;
  emit();
  return restore.length;
}

// ── Cloud backup pull (myPokemonBackup.ts / MyPokemonSyncer) ─────────────────

export interface BackupPullResult {
  /** Server records adopted (new locally, or newer than the local copy). */
  adopted: number;
  /** Local records removed because a newer server tombstone says deleted. */
  removed: number;
}

/**
 * Merge pulled backup rows into the local store, record by record, LWW on the
 * DEVICE-clock updatedAt (the same adjudication the server ran on push).
 * Payloads run through normalizeRecord, so a corrupt or stale-schema server
 * row can never plant a malformed record locally. Local records the server
 * has never seen are untouched (the syncer pushes them right after).
 */
export function applyMyPokemonBackupPull(
  pulled: ReadonlyArray<{ payload: Record<string, unknown> | null; deleted: boolean; id: string; updatedAt: number }>,
): BackupPullResult {
  const records = readStore();
  const byId = new Map(records.map((r) => [r.id, r] as const));
  let adopted = 0;
  let removed = 0;
  for (const row of pulled) {
    const local = byId.get(row.id);
    if (row.deleted) {
      // A tombstone only beats a local record it is NEWER than: a record the
      // trainer re-created (undo bumps updatedAt) survives its own deletion.
      if (local && row.updatedAt > local.updatedAt) {
        byId.delete(row.id);
        removed += 1;
      }
      continue;
    }
    if (!row.payload) continue;
    const incoming = normalizeRecord({ ...row.payload, id: row.id });
    if (!incoming) continue;
    if (!local || incoming.updatedAt > local.updatedAt) {
      byId.set(row.id, incoming);
      adopted += 1;
    }
  }
  if (adopted === 0 && removed === 0) return { adopted, removed };
  if (!writeStore([...byId.values()])) return { adopted: 0, removed: 0 };
  emit();
  return { adopted, removed };
}

export function clearMyPokemon(): number {
  const count = readStore().length;
  writeStore([]);
  emit();
  return count;
}

// ── React subscription ───────────────────────────────────────────────────────

let snapshotCache: MyPokemonRecord[] | null = null;

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => {
    snapshotCache = null;
    cb();
  };
  window.addEventListener(MY_POKEMON_EVENT, handler);
  return () => window.removeEventListener(MY_POKEMON_EVENT, handler);
}

function getSnapshot(): MyPokemonRecord[] {
  if (!snapshotCache) snapshotCache = listMyPokemon();
  return snapshotCache;
}

const EMPTY: MyPokemonRecord[] = [];
function getServerSnapshot(): MyPokemonRecord[] {
  return EMPTY;
}

/** Live list of records, newest-updated first. */
export function useMyPokemonRecords(): MyPokemonRecord[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Test seam: drop the module caches so each test starts cold. */
export function __resetMyPokemonStoreForTest(): void {
  cache = null;
  snapshotCache = null;
  lastDeletion = null;
  hydrated = false;
  hydrating = null;
}
